// -- copyright
// OpenProject is an open source project management software.
// Copyright (C) the OpenProject GmbH
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License version 3.
//
// OpenProject is a fork of ChiliProject, which is a fork of Redmine. The copyright follows:
// Copyright (C) 2006-2013 Jean-Philippe Lang
// Copyright (C) 2010-2013 the ChiliProject Team
//
// This program is free software; you can redistribute it and/or
// modify it under the terms of the GNU General Public License
// as published by the Free Software Foundation; either version 2
// of the License, or (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program; if not, write to the Free Software
// Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
//
// See COPYRIGHT and LICENSE files for more details.
// ++

import { DisplayField } from 'core-app/shared/components/fields/display/display-field.module';

interface ProjectAncestorLink {
  href:string;
  title:string;
}

/**
 * A project shown with the projects it sits under, rather than by its own name
 * alone: "Parent / Child / This one". In a hierarchy of projects the leaf name
 * on its own is often not enough to say which project is meant - several can
 * share a name under different parents.
 *
 * The ancestors are read from the project's own _links.ancestors, which the API
 * already sends with every project, so nothing extra is fetched.
 *
 * They are rendered as plain text rather than as links: this field sits inside
 * an inline-editable container, where a click opens the editor, and a link
 * inside it would have to fight that for the same click.
 */
export class ProjectDisplayField extends DisplayField {
  public get value():string|null {
    if (this.schema) {
      return this.attribute && this.attribute.name;
    }

    return null;
  }

  public render(element:HTMLElement, displayText:string):void {
    const { ancestors } = this;

    if (ancestors.length === 0) {
      super.render(element, displayText);
      return;
    }

    element.textContent = '';
    element.classList.add('op-project-hierarchy');

    ancestors.forEach((ancestor) => {
      element.appendChild(this.crumb(ancestor.title, 'op-project-hierarchy--ancestor'));
      element.appendChild(this.separator());
    });

    element.appendChild(this.crumb(displayText, 'op-project-hierarchy--name'));
  }

  private crumb(text:string, className:string):HTMLElement {
    const crumb = document.createElement('span');
    crumb.classList.add(className);
    crumb.textContent = text;

    return crumb;
  }

  private separator():HTMLElement {
    const separator = document.createElement('span');
    separator.classList.add('op-project-hierarchy--separator');
    separator.setAttribute('aria-hidden', 'true');
    separator.textContent = '/';

    return separator;
  }

  private get ancestors():ProjectAncestorLink[] {
    const source = (this.attribute as undefined|{ $source?:{ _links?:{ ancestors?:ProjectAncestorLink[] } } })?.$source;
    const links = source?._links?.ancestors;

    return Array.isArray(links) ? links : [];
  }
}
